import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store/useStore.js';
import { NEON_PALETTE } from '../utils/constants.js';

export function CanvasContextMenu() {
  const ctx = useStore((s) => s.contextMenu);
  const setContextMenu = useStore((s) => s.setContextMenu);
  const nodes = useStore((s) => s.nodes);
  const links = useStore((s) => s.links);

  const addNode = useStore((s) => s.addNode);
  const deleteNode = useStore((s) => s.deleteNode);
  const duplicateNode = useStore((s) => s.duplicateNode);
  const updateNode = useStore((s) => s.updateNode);
  const toggleNodeLock = useStore((s) => s.toggleNodeLock);
  const createLink = useStore((s) => s.createLink);
  const removeLink = useStore((s) => s.removeLink);
  const setSelected = useStore((s) => s.setSelected);
  const setMultiSelected = useStore((s) => s.setMultiSelected);
  const zoomToFit = useStore((s) => s.zoomToFit);

  const [colorOpen, setColorOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ctx) { setColorOpen(false); setTypeOpen(false); return; }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ctx]);

  if (!ctx) return null;

  function close() { setContextMenu(null); }

  const node = ctx.nodeId ? nodes.find((n) => n.id === ctx.nodeId) : null;
  const link = ctx.linkId ? links.find((l) => l.id === ctx.linkId) : null;

  // Clamp menu to viewport.
  const menuW = 200;
  const menuH = 280;
  const x = Math.min(ctx.x, window.innerWidth - menuW - 8);
  const y = Math.min(ctx.y, window.innerHeight - menuH - 8);

  function MenuItem({
    children, onClick, danger, sub,
  }: { children: React.ReactNode; onClick?: () => void; danger?: boolean; sub?: boolean }) {
    return (
      <button
        onClick={() => { onClick?.(); if (!sub) close(); }}
        style={{
          width: '100%', textAlign: 'left',
          padding: '7px 12px', border: 'none', borderRadius: 6,
          background: 'transparent',
          color: danger ? '#f87171' : 'var(--text)',
          fontSize: 12, fontWeight: 500, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          transition: 'background 0.1s',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
      >
        {children}
      </button>
    );
  }

  function Divider() {
    return <div style={{ height: 1, background: 'var(--border)', margin: '3px 0' }} />;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        style={{ position: 'fixed', inset: 0, zIndex: 490 }}
      />
      {/* Menu */}
      <div
        ref={menuRef}
        className="glass-panel"
        style={{
          position: 'fixed',
          left: x, top: y,
          minWidth: menuW,
          borderRadius: 10,
          padding: '4px',
          zIndex: 500,
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
        }}
      >
        {node && (
          <>
            <div style={{ padding: '6px 12px 4px', fontSize: 10, color: 'var(--muted)', fontWeight: 600, letterSpacing: '0.06em' }}>
              NÓ: {node.label.slice(0, 24)}
            </div>
            <Divider />
            <MenuItem onClick={() => { setSelected(ctx.nodeId!); }}>
              Editar <span style={{ color: 'var(--hint)', fontSize: 10 }}>↵</span>
            </MenuItem>
            <MenuItem onClick={() => duplicateNode(ctx.nodeId!)}>
              Duplicar
            </MenuItem>

            {/* Color submenu */}
            <MenuItem sub onClick={() => { setColorOpen((v) => !v); setTypeOpen(false); }}>
              Cor
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: node.color, display: 'inline-block' }} />
                ›
              </span>
            </MenuItem>
            {colorOpen && (
              <div style={{ padding: '6px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {NEON_PALETTE.map((c) => (
                  <button
                    key={c}
                    onClick={() => { updateNode(ctx.nodeId!, { color: c }); close(); }}
                    style={{
                      width: 18, height: 18, borderRadius: '50%',
                      background: c, border: node.color === c ? '2px solid white' : '2px solid transparent',
                      cursor: 'pointer', padding: 0,
                    }}
                  />
                ))}
              </div>
            )}

            {/* Type submenu */}
            <MenuItem sub onClick={() => { setTypeOpen((v) => !v); setColorOpen(false); }}>
              Tipo <span style={{ color: 'var(--hint)', fontSize: 10 }}>{node.type} ›</span>
            </MenuItem>
            {typeOpen && (
              <div style={{ paddingLeft: 8, paddingBottom: 4 }}>
                {(['idea', 'text', 'doc', 'url', 'universe'] as const).map((t) => (
                  <MenuItem key={t} onClick={() => { updateNode(ctx.nodeId!, { type: t }); close(); }}>
                    <span style={{ color: node.type === t ? 'var(--neon)' : 'var(--text)' }}>{t}</span>
                    {node.type === t && <span style={{ color: 'var(--neon)', fontSize: 10 }}>✓</span>}
                  </MenuItem>
                ))}
              </div>
            )}

            <MenuItem onClick={() => toggleNodeLock(ctx.nodeId!)}>
              {node.locked ? 'Desbloquear' : 'Bloquear'}
            </MenuItem>
            <Divider />
            <MenuItem danger onClick={() => deleteNode(ctx.nodeId!)}>
              Apagar nó
            </MenuItem>
          </>
        )}

        {link && !node && (
          <>
            <div style={{ padding: '6px 12px 4px', fontSize: 10, color: 'var(--muted)', fontWeight: 600, letterSpacing: '0.06em' }}>
              CONEXÃO
            </div>
            <Divider />
            <MenuItem onClick={() => {
              const newType = link.type === 'wiki' ? 'synapse' : 'wiki';
              useStore.getState().snapshot();
              useStore.setState((s) => ({
                links: s.links.map((l) => l.id === link.id ? { ...l, type: newType } : l),
              }));
            }}>
              Tipo: <span style={{ color: 'var(--neon)', marginLeft: 6 }}>{link.type}</span>
              <span style={{ color: 'var(--hint)', fontSize: 10 }}>(clique para trocar)</span>
            </MenuItem>
            <Divider />
            <MenuItem danger onClick={() => removeLink(link.source, link.target)}>
              Apagar conexão
            </MenuItem>
          </>
        )}

        {!node && !link && (
          <>
            <MenuItem onClick={() => {
              const id = addNode({
                label: 'Nova Ideia', type: 'idea', category: 'idea', icon: 'lightbulb',
                x: ctx.worldX, y: ctx.worldY, size: 22,
              });
              setTimeout(() => setSelected(id), 10);
            }}>
              Novo nó aqui
            </MenuItem>
            <MenuItem onClick={() => zoomToFit()}>
              Centralizar grafo <span style={{ color: 'var(--hint)', fontSize: 10 }}>F</span>
            </MenuItem>
            <MenuItem onClick={() => {
              const st = useStore.getState();
              setMultiSelected(new Set(st.nodes.map((n) => n.id)));
            }}>
              Selecionar todos
            </MenuItem>
          </>
        )}
      </div>
    </>
  );
}
